import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '@iconify/react';
import { decodeData } from '../../../utils/encodeAndEncode';
import { singleSurveyDetails } from '../../../services/adminService';
import { toast } from 'react-toastify';

function CourseAnalyticsSurveyDetails() {
  const { surveyId, courseId } = useParams();
  const navigate = useNavigate();
  const decodedCourseId = courseId ? decodeData(courseId) : null;
  const decodedSurveyId = surveyId ? decodeData(surveyId) : null;

  const [surveyDetails, setSurveyDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSurveyDetails();
  }, [decodedSurveyId, decodedCourseId]);

  const fetchSurveyDetails = async () => {
    try {
      setLoading(true);

      // Commented multi-line console.log);

      const response = await singleSurveyDetails({
          assessment_id: decodedSurveyId,
        course_id: decodedCourseId
      });

      // console.log('📋 Survey Details Response:', response);

      if (response.success) {
        // Handle both cases: data exists or is empty array
        const surveyData = response.data || [];
        const totalQuestions = surveyData.length;
        let totalAttempts = 0;
        let totalRating = 0;
        let ratingCount = 0;

        surveyData.forEach(question => {
          totalAttempts += question.responses ? question.responses.length : 0;
          // If responses contain ratings, calculate average
          if (question.responses) {
            question.responses.forEach(resp => {
              if (resp.answer && !isNaN(resp.answer)) {
                totalRating += parseFloat(resp.answer);
                ratingCount++;
              }
            });
          }
        });

        const averageRating = ratingCount > 0 ? (totalRating / ratingCount).toFixed(1) : 0;
        const completionRate = totalQuestions > 0 ? Math.round((totalAttempts / totalQuestions) * 100) : 0;

        setSurveyDetails({
          id: decodedSurveyId,
          name: 'Survey Details',
          questions: totalQuestions,
          attempts: totalAttempts,
          averageRating: averageRating,
          completionRate: completionRate,
          data: surveyData,
          message: response.message || null
        });
      } else {
        console.error('❌ Survey details API failed:', response);
        toast.error(response.message || 'Failed to load survey details', {
          position: 'top-center',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error fetching survey details:', error);
      toast.error('Failed to fetch survey details', {
        position: 'top-center',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!surveyDetails) {
    return (
      <div className="text-center py-4">
        <p>No survey details found</p>
        <button
          className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
          onClick={() => navigate(-1)}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className="mb-4">
        <div className="flex items-center gap-4 mb-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            aria-label="Go back"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" />
          </button>
          <div>
            <h4 className="text-xl font-semibold text-gray-900 mb-0.5">Survey Analytics</h4>
            <p className="text-sm text-gray-500 mb-0">Detailed question responses and statistics</p>
          </div>
        </div>
      </div>

      {/* Assessment Overview */}
      <div className="mb-4">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Total Questions</div>
                  <div className="text-2xl font-semibold mb-0">{surveyDetails.questions}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Total Attempts</div>
                  <div className="text-2xl font-semibold mb-0">{surveyDetails.attempts}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Average Rating</div>
                  <div className="text-2xl font-semibold mb-0">{surveyDetails.averageRating}/5.0</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-gray-500 mb-1 text-sm">Completion Rate</div>
                  <div className="text-2xl font-semibold mb-0">{surveyDetails.completionRate}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Survey Questions and Responses */}
      <div>
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-4">
              <h5 className="text-xl font-semibold mb-4">Survey Questions & Responses</h5>

              {surveyDetails.data && surveyDetails.data.length > 0 ? (
                surveyDetails.data.map((question, index) => (
                  <div key={index} className="mb-4 p-3 border rounded-lg">
                    <h6 className="mb-3 font-semibold">
                      <span className="bg-primary text-white px-2 py-1 rounded mr-2 text-sm">{index + 1}</span>
                      {question.question}
                    </h6>

                    {question.responses && question.responses.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="py-2 px-3 text-left">Name</th>
                              <th className="py-2 px-3 text-left">Email</th>
                              <th className="py-2 px-3 text-left">Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {question.responses.map((response, respIndex) => (
                              <tr key={respIndex} className="border-b">
                                <td className="py-2 px-3">{response.name || 'N/A'}</td>
                                <td className="py-2 px-3">{response.email || 'N/A'}</td>
                                <td className="py-2 px-3">{response.answer || 'No response'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500">No responses yet</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <Icon icon="fluent:clipboard-text-24-regular" width="48" height="48" className="text-gray-500 mb-3" />
                  <h6 className="text-gray-500">No Questions Found</h6>
                  <p className="text-gray-500 mb-0">
                    {surveyDetails.message || 'This survey does not have any questions yet.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CourseAnalyticsSurveyDetails;
